/**
 * Add to question: pick which question the selected text answers. A question
 * that already has an answer asks in the row first, because the selection
 * moves to the end of that answer; one without an answer takes the selection
 * as its answer in place.
 */
import { ArrowLeftIcon, SearchIcon } from "@/design/icons";
import { type FC, useMemo, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { Input } from "@/design/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";

import { NUMBER_CLASS, numberColumnStyle } from "./numberColumn";
import type { Question } from "../tender/questionsApi";

export type QuestionPickerProps = {
  open: boolean;
  selectionWords: number;
  questions: Question[];
  inDocument: (sectionId: string) => boolean;
  onClose: () => void;
  onPick: (question: Question) => Promise<void>;
};

export const QuestionPicker: FC<QuestionPickerProps> = (props) => (
  <Sheet open={props.open} onOpenChange={(o) => !o && props.onClose()}>
    <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
      {props.open && <Body {...props} />}
    </SheetContent>
  </Sheet>
);

const Body: FC<QuestionPickerProps> = ({ selectionWords, questions, inDocument, onClose, onPick }) => {
  const [query, setQuery] = useState("");
  const [confirm, setConfirm] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? questions.filter((x) => `${x.section_number} ${x.heading} ${x.question}`.toLowerCase().includes(q))
      : questions;
  }, [questions, query]);

  const numberStyle = numberColumnStyle(questions.map((x) => x.section_number));

  const pick = async (question: Question) => {
    setBusy(true);
    try {
      await onPick(question);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
        <button
          type="button"
          aria-label="Back"
          onClick={onClose}
          className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
        >
          <ArrowLeftIcon className="size-4" />
        </button>
        <span className="text-sm font-medium text-fg-3">Add to question</span>
      </div>
      <div className="px-4 pb-2 pt-4">
        <SheetTitle className="text-lg font-semibold leading-snug text-fg">Which question does this answer?</SheetTitle>
        <SheetDescription className="mt-0.5 text-sm text-fg-3">
          {selectionWords} selected word{selectionWords === 1 ? "" : "s"}. They become part of that question&apos;s
          answer.
        </SheetDescription>
      </div>
      <div className="px-3 pb-2">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-3" />
          <Input
            id="question-search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions"
            className="h-[34px] bg-subtle pl-8"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {shown.map((q, i) => {
          const has = inDocument(q.section_id);
          if (confirm === q.section_id) {
            return (
              <div
                key={q.section_id}
                className={cn("flex items-center gap-2.5 bg-accent-soft px-3 py-2", i && "border-t border-line/70")}
              >
                <span className={NUMBER_CLASS} style={numberStyle}>
                  {q.section_number}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{q.heading}</span>
                  <span className="block text-xs text-fg-2">Move the selection to the end of this answer?</span>
                </span>
                <button
                  type="button"
                  disabled={busy}
                  className="rounded-sm bg-fg px-2.5 py-1 text-xs font-semibold text-fg-inverse"
                  onClick={() => void pick(q)}
                >
                  Move
                </button>
                <button
                  type="button"
                  className="rounded-sm px-2 py-1 text-xs font-semibold text-fg-2 hover:bg-accent-soft-2"
                  onClick={() => setConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            );
          }
          return (
            <button
              key={q.section_id}
              type="button"
              disabled={busy}
              onClick={() => (has ? setConfirm(q.section_id) : void pick(q))}
              className={cn(
                "flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-subtle",
                i && "border-t border-line/70"
              )}
            >
              <span className={NUMBER_CLASS} style={numberStyle}>
                {q.section_number}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{q.heading || q.question}</span>
                <span className="block text-xs text-fg-3">
                  {has
                    ? "Has an answer · selection moves to its end"
                    : "Not in document · selection becomes its answer"}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
